import { useQuery } from '@tanstack/react-query';
import { Link as LinkIcon, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { buildToc, type DocumentContent, type DocumentSummaryDto, type Paginated } from '@imsenta/shared';
import { api } from '@/lib/api';
import { Badge, Button, Field, Input, Modal, Select, Spinner } from '@/components/ui';

export interface CrossReferenceValue {
  documentId: string | null;
  documentRef: string | null;
  sectionAnchor: string | null;
  label: string;
  url: string | null;
}

/**
 * Lets an author link to another document, a section of it, a section of the
 * current document, or an external URL — without typing any Markdown.
 */
export function CrossReferencePicker({
  open,
  onClose,
  onInsert,
  currentContent,
  currentDocumentId,
}: {
  open: boolean;
  onClose: () => void;
  onInsert: (value: CrossReferenceValue) => void;
  currentContent: DocumentContent;
  currentDocumentId?: string;
}) {
  const [mode, setMode] = useState<'document' | 'section' | 'external'>('document');
  const [search, setSearch] = useState('');
  const [selectedDocId, setSelectedDocId] = useState<string>('');
  const [anchor, setAnchor] = useState('');
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['xref-documents', search],
    queryFn: () => api.get<Paginated<DocumentSummaryDto>>('/documents', { q: search || undefined, pageSize: 25 }),
    enabled: open && mode === 'document',
  });

  const documents = useMemo(() => (data?.items ?? []).filter((d) => d.id !== currentDocumentId), [data, currentDocumentId]);
  const selectedDoc = documents.find((d) => d.id === selectedDocId) ?? null;

  const { data: target } = useQuery({
    queryKey: ['xref-target', selectedDocId],
    queryFn: () => api.get<{ published: { content: DocumentContent } | null; draft: { content: DocumentContent } | null }>(`/documents/${selectedDocId}`),
    enabled: open && mode === 'document' && Boolean(selectedDocId),
  });

  const targetToc = useMemo(() => {
    const content = target?.published?.content ?? target?.draft?.content;
    return content ? buildToc(content, { skipInternal: true }) : [];
  }, [target]);

  const localToc = useMemo(() => buildToc(currentContent, { skipInternal: true }), [currentContent]);

  const reset = () => {
    setSelectedDocId('');
    setAnchor('');
    setLabel('');
    setUrl('');
    setSearch('');
  };

  const insert = () => {
    if (mode === 'external') {
      if (!/^https?:\/\//i.test(url)) return;
      onInsert({ documentId: null, documentRef: null, sectionAnchor: null, label: label.trim() || url, url });
    } else if (mode === 'section') {
      if (!anchor) return;
      const entry = localToc.find((t) => t.anchor === anchor);
      onInsert({
        documentId: null,
        documentRef: null,
        sectionAnchor: anchor,
        label: label.trim() || (entry ? `${entry.number ? `${entry.number} ` : ''}${entry.title}` : anchor),
        url: null,
      });
    } else {
      if (!selectedDoc) return;
      const entry = targetToc.find((t) => t.anchor === anchor);
      const suffix = entry ? ` §${entry.number || entry.title}` : '';
      onInsert({
        documentId: selectedDoc.id,
        documentRef: selectedDoc.reference,
        sectionAnchor: anchor || null,
        label: label.trim() || `${selectedDoc.title}${suffix}`,
        url: null,
      });
    }
    reset();
    onClose();
  };

  const canInsert = mode === 'external' ? /^https?:\/\//i.test(url) : mode === 'section' ? Boolean(anchor) : Boolean(selectedDocId);

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title="Insert a cross reference"
      description="References follow the target if it is renamed, and resolve to a link in every export."
      width="lg"
      footer={
        <>
          <Button
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button variant="primary" icon={<LinkIcon className="size-4" />} onClick={insert} disabled={!canInsert}>
            Insert reference
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex gap-1 rounded-lg bg-ink-100 p-1">
          {(
            [
              ['document', 'Another document'],
              ['section', 'Section of this document'],
              ['external', 'External link'],
            ] as const
          ).map(([id, text]) => (
            <button
              key={id}
              type="button"
              onClick={() => {
                setMode(id);
                setAnchor('');
              }}
              className={`flex-1 rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors ${
                mode === id ? 'bg-white text-ink-900 shadow-xs' : 'text-ink-500 hover:text-ink-800'
              }`}
            >
              {text}
            </button>
          ))}
        </div>

        {mode === 'document' ? (
          <>
            <Field label="Find a document">
              <div className="relative">
                <Search className="absolute top-2.5 left-3 size-4 text-ink-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by title or reference…" className="pl-9" />
              </div>
            </Field>

            <div className="max-h-56 overflow-y-auto rounded-lg border border-ink-200">
              {isLoading ? (
                <Spinner />
              ) : documents.length === 0 ? (
                <p className="px-4 py-6 text-center text-[13px] text-ink-500">No other documents found.</p>
              ) : (
                <ul className="divide-y divide-ink-100">
                  {documents.map((doc) => (
                    <li key={doc.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedDocId(doc.id);
                          setAnchor('');
                        }}
                        className={`flex w-full items-center gap-3 px-3.5 py-2.5 text-left transition-colors ${
                          selectedDocId === doc.id ? 'bg-brand-50' : 'hover:bg-ink-50'
                        }`}
                      >
                        <Badge tone="neutral">{doc.reference}</Badge>
                        <span className="min-w-0 flex-1 truncate text-[13.5px] font-medium text-ink-800">{doc.title}</span>
                        <span className="text-[11.5px] text-ink-400">{doc.documentTypeName}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {selectedDoc ? (
              <Field label="Link to a specific section (optional)">
                <Select value={anchor} onChange={(e) => setAnchor(e.target.value)}>
                  <option value="">Whole document</option>
                  {targetToc.map((entry) => (
                    <option key={entry.anchor} value={entry.anchor}>
                      {'— '.repeat(Math.max(0, entry.depth - 1))}
                      {entry.number ? `${entry.number} ` : ''}
                      {entry.title}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : null}
          </>
        ) : null}

        {mode === 'section' ? (
          <Field label="Section in this document" required>
            <Select value={anchor} onChange={(e) => setAnchor(e.target.value)}>
              <option value="">Choose a section…</option>
              {localToc.map((entry) => (
                <option key={entry.anchor} value={entry.anchor}>
                  {'— '.repeat(Math.max(0, entry.depth - 1))}
                  {entry.number ? `${entry.number} ` : ''}
                  {entry.title}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}

        {mode === 'external' ? (
          <Field label="Web address" required hint="Must start with https:// or http://">
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.iso.org/standard/27001" />
          </Field>
        ) : null}

        <Field label="Link text" hint="Leave blank to use the document or section name.">
          <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Auto" />
        </Field>
      </div>
    </Modal>
  );
}
